/**
 * SelectionTable Component
 *
 * TanStack Table with virtualization support for selecting items.
 * Used in wizard modals for vulnerability/asset selection.
 */

import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Loader } from '@/components/Loader';
import { InfiniteScrollIndicator } from '@/components/table/InfiniteScrollIndicator';
import type { SearchableListItem } from '@/sections/attacks/attackPaths/components/wizards/hooks';
import { cn } from '@/utils/classname';

/**
 * Props for SelectionTable component
 */
export interface SelectionTableProps {
  /** Data to display */
  data: SearchableListItem[];
  /** Column definitions */
  columns: ColumnDef<SearchableListItem>[];
  /** Currently selected item key */
  selectedKey: string | null;
  /** Selection callback */
  onSelect: (item: SearchableListItem) => void;
  /** Loading state */
  isLoading?: boolean;
  /** Whether more pages are available */
  hasNextPage?: boolean;
  /** Whether currently fetching next page */
  isFetchingNextPage?: boolean;
  /** Callback to load more data */
  onLoadMore?: () => void;
  /** Default sort state */
  defaultSort?: SortingState;
  /** Threshold for enabling virtualization (default 50) */
  virtualizationThreshold?: number;
  /** Maximum visible rows (default 8, which gives ~400px height) */
  maxRows?: number;
  /** Explicit container height override in pixels */
  containerHeight?: number;
  /**
   * Whether to visually highlight the selected row.
   * When false, selectedKey is still used for auto-scroll and semantic state,
   * but the row does not get visual highlight styling.
   * Useful in edit mode where selection is shown via "Currently Editing" display.
   * @default true
   */
  highlightSelected?: boolean;
}

/**
 * Row height in pixels
 */
const ROW_HEIGHT = 44;

/**
 * Table header height in pixels.
 * The header is inside the scroll container, so we must account for it.
 * Composed of: py-2 (16px) + text-xs (~12px) + line-height (~4px) = ~32px
 */
const TABLE_HEADER_HEIGHT = 32;

/**
 * Scroll threshold for loading more (pixels from bottom)
 */
const SCROLL_THRESHOLD = 100;

/**
 * SelectionTable displays a sortable, selectable table with optional virtualization.
 *
 * Features:
 * - TanStack Table for sorting and column management
 * - Virtual scrolling for large datasets
 * - Row selection with highlight
 * - Infinite scroll support
 *
 * @example
 * ```tsx
 * <SelectionTable
 *   data={items}
 *   columns={vulnerabilityColumns}
 *   selectedKey={selectedId}
 *   onSelect={handleSelect}
 * />
 * ```
 */
export function SelectionTable({
  data,
  columns,
  selectedKey,
  onSelect,
  isLoading = false,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  defaultSort = [],
  virtualizationThreshold = 50,
  maxRows = 8,
  containerHeight,
  highlightSelected = true,
}: SelectionTableProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [sorting, setSorting] = useState<SortingState>(defaultSort);

  // Determine if pagination is possible (props were provided)
  const hasPaginationSupport = onLoadMore !== undefined;

  // Calculate container height from maxRows or explicit override
  // Include TABLE_HEADER_HEIGHT because the header is inside the scroll container
  const calculatedHeight =
    containerHeight ?? maxRows * ROW_HEIGHT + TABLE_HEADER_HEIGHT;

  // Initialize TanStack Table
  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: row => row.key,
  });

  const { rows } = table.getRowModel();

  // Determine if virtualization should be enabled
  const useVirtualization = data.length > virtualizationThreshold;

  // Virtual row management
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 5,
    enabled: useVirtualization,
  });

  // Auto-scroll to selected item when data loads or selection changes (edit mode)
  useEffect(() => {
    // Skip if no selection or no data
    if (!selectedKey || rows.length === 0) return;

    // Find the index of the selected row in the sorted/filtered table data
    const selectedIndex = rows.findIndex(row => row.id === selectedKey);

    // If item not found (filtered out or custom), don't scroll
    if (selectedIndex === -1) return;

    // Use requestAnimationFrame to ensure layout is complete before scrolling
    const scrollFrame = requestAnimationFrame(() => {
      if (useVirtualization && virtualizer) {
        // Virtualized mode: use virtualizer's scrollToIndex for efficiency
        virtualizer.scrollToIndex(selectedIndex, { align: 'center' });
      } else {
        // Non-virtualized mode: use native DOM scrollIntoView
        const row = scrollContainerRef.current?.querySelector(
          `[data-testid="selection-table-row-${selectedKey}"]`
        );
        if (row) {
          row.scrollIntoView({ block: 'center', behavior: 'instant' });
        }
      }
    });

    // Cleanup: cancel animation frame if effect re-runs or unmounts
    return () => cancelAnimationFrame(scrollFrame);
  }, [selectedKey, rows, useVirtualization, virtualizer]);

  const virtualRows = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    if (!onLoadMore || !hasNextPage || isFetchingNextPage) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    if (distanceFromBottom < SCROLL_THRESHOLD) {
      onLoadMore();
    }
  }, [onLoadMore, hasNextPage, isFetchingNextPage]);

  // Loading state
  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center"
        style={{ height: calculatedHeight }}
        data-testid="selection-table-loading"
      >
        <Loader type="spinner" isLoading />
      </div>
    );
  }

  // Empty state
  if (data.length === 0) {
    return (
      <div
        className="text-chariot-text-secondary flex items-center justify-center"
        style={{ height: calculatedHeight }}
      >
        No results found
      </div>
    );
  }

  /**
   * Render a data row with proper styling.
   * @param row - The table row data
   * @param style - Optional inline styles (used for virtualization height)
   * @param showTopBorder - Whether to show top border (false for first visible row)
   */
  const renderRow = (
    row: (typeof rows)[0],
    style?: CSSProperties,
    showTopBorder = true
  ) => {
    // Semantic: Does this row match the selected key? (for auto-scroll + ARIA)
    const isMatchingSelection = row.id === selectedKey;

    // Visual: Should we apply highlight styling? (respects highlightSelected prop)
    const shouldHighlight = highlightSelected && isMatchingSelection;

    return (
      <tr
        key={row.id}
        data-testid={`selection-table-row-${row.id}`}
        onClick={() => onSelect(row.original)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect(row.original);
          }
        }}
        style={style}
        tabIndex={0}
        role="option"
        aria-selected={isMatchingSelection}
        className={cn(
          'group cursor-pointer transition-colors duration-150',
          'hover:bg-chariot-background-secondary',
          'focus:ring-chariot-brand-primary outline-hidden focus:ring-2 focus:ring-inset',
          // Border on top of row (except first visible row)
          showTopBorder && 'border-chariot-stroke border-t',
          shouldHighlight &&
            'is-selected bg-chariot-stroke text-chariot-text-primary'
        )}
      >
        {row.getVisibleCells().map(cell => (
          <td key={cell.id} className="px-3 py-1">
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        ))}
      </tr>
    );
  };

  return (
    <div className="flex flex-col">
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        data-testid="selection-table-scroll-container"
        className="border-chariot-stroke bg-chariot-background-primary overflow-auto rounded-lg border"
        style={{ height: calculatedHeight }}
      >
        <table className="w-full table-fixed text-sm" role="table">
          {/* colgroup enforces column widths - columns without explicit size are flexible */}
          <colgroup>
            {table.getAllColumns().map(column => {
              const size = column.getSize();
              // Use explicit meta.flexible marker instead of relying on TanStack Table internals
              const isFlexible =
                (column.columnDef.meta as { flexible?: boolean })?.flexible ??
                false;

              return (
                <col
                  key={column.id}
                  style={
                    isFlexible
                      ? undefined // Flexible: takes remaining space
                      : {
                          width: size,
                          minWidth: size,
                          maxWidth: size,
                        }
                  }
                />
              );
            })}
          </colgroup>
          <thead className="bg-chariot-background-primary sticky top-0 z-10">
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const isSorted = header.column.getIsSorted();

                  return (
                    <th
                      key={header.id}
                      className={cn(
                        'bg-chariot-background-primary text-chariot-text-secondary px-3 py-2 text-left text-xs font-semibold uppercase',
                        header.column.getCanSort() &&
                          'cursor-pointer select-none'
                      )}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div className="flex items-center gap-1">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {isSorted === 'asc' && (
                          <ChevronUpIcon className="size-3" />
                        )}
                        {isSorted === 'desc' && (
                          <ChevronDownIcon className="size-3" />
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {useVirtualization ? (
              // Virtualized rendering with spacer rows for scroll positioning
              <>
                {/* Top spacer - only render if there's space to fill */}
                {virtualRows.length > 0 && virtualRows[0].start > 0 && (
                  <tr
                    style={{ height: virtualRows[0].start }}
                    aria-hidden="true"
                  >
                    <td colSpan={columns.length} />
                  </tr>
                )}
                {/* Visible rows */}
                {virtualRows.map((virtualRow, index) => {
                  const row = rows[virtualRow.index];
                  // First visible row has no top border if spacer exists, otherwise check if it's the first row overall
                  const isFirstVisible =
                    index === 0 && virtualRows[0].start === 0;
                  return renderRow(
                    row,
                    { height: ROW_HEIGHT },
                    !isFirstVisible
                  );
                })}
                {/* Bottom spacer - only render if there's space to fill */}
                {(() => {
                  const bottomHeight =
                    totalSize - (virtualRows[virtualRows.length - 1]?.end ?? 0);
                  return (
                    virtualRows.length > 0 &&
                    bottomHeight > 0 && (
                      <tr style={{ height: bottomHeight }} aria-hidden="true">
                        <td colSpan={columns.length} />
                      </tr>
                    )
                  );
                })()}
              </>
            ) : (
              // Regular rendering for small datasets
              rows.map((row, index) => renderRow(row, undefined, index > 0))
            )}
          </tbody>
        </table>
      </div>

      {/* Scroll indicator using shared component with relative variant */}
      {hasPaginationSupport && (
        <InfiniteScrollIndicator
          variant="relative"
          isLoading={isFetchingNextPage}
          hasMore={hasNextPage === true}
          itemCount={data.length}
          minItemsThreshold={10}
        />
      )}
    </div>
  );
}
